/**
 * PDF Viewer Component
 * Simple PDF display using iframe or embed for viewing PDFs
 * Validates PDF URL before rendering and shows fallback on failure
 */

import { useState, useEffect } from 'react'
import { FileText, Download, ExternalLink, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils/cn'

interface PDFViewerProps {
  url: string
  title?: string
  className?: string
  height?: string | number
  showToolbar?: boolean
  onDownload?: () => void
}

export function PDFViewer({
  url,
  title = 'Document',
  className,
  height = 600,
  showToolbar = true,
  onDownload,
}: PDFViewerProps) {
  const [loadError, setLoadError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [urlValid, setUrlValid] = useState<boolean | null>(null)

  // Skip HEAD validation — Firebase Storage and many CDNs block it via CORS.
  // Instead, trust the URL and let the iframe handle load/error.
  useEffect(() => {
    if (!url) {
      setLoadError(true)
      setIsLoading(false)
      setUrlValid(false)
      return
    }

    setIsLoading(true)
    setLoadError(false)
    setUrlValid(true)
  }, [url])

  const handleIframeLoad = () => {
    setIsLoading(false)
  }

  const handleIframeError = () => {
    setIsLoading(false)
    setLoadError(true)
  }

  const openInNewTab = () => {
    window.open(url, '_blank')
  }

  if (loadError) {
    return (
      <div className={cn('border dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 p-8', className)}>
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Unable to display PDF
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            The document could not be loaded inline. You can open it directly or download it.
          </p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={openInNewTab}>
              <ExternalLink className="h-4 w-4 mr-2" />
              Open in New Tab
            </Button>
            {onDownload && (
              <Button onClick={onDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('border dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900', className)}>
      {showToolbar && (
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{title}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={openInNewTab}>
              <ExternalLink className="h-4 w-4" />
            </Button>
            {onDownload && (
              <Button variant="ghost" size="sm" onClick={onDownload}>
                <Download className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      )}

      <div
        className="relative overflow-auto overscroll-contain touch-pan-y"
        style={{ height: typeof height === 'number' ? `${height}px` : height }}
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-800">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading document...</p>
            </div>
          </div>
        )}

        {urlValid && (
          <iframe
            src={`${url}#toolbar=0&navpanes=0&scrollbar=1`}
            className="w-full h-full"
            title={title}
            scrolling="yes"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        )}
      </div>
    </div>
  )
}

/**
 * Compact PDF preview for lists/cards
 */
interface PDFThumbnailProps {
  url: string
  title?: string
  className?: string
  onClick?: () => void
}

export function PDFThumbnail({
  url,
  title = 'Document',
  className,
  onClick,
}: PDFThumbnailProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group relative border dark:border-gray-700 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors',
        'w-full aspect-[8.5/11]', // Standard letter paper ratio
        className
      )}
    >
      <iframe
        src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
        className="w-full h-full pointer-events-none"
        title={title}
        tabIndex={-1}
      />
      <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors">
        <div className="bg-white/90 dark:bg-gray-800/90 px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow">
          <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Click to view</span>
        </div>
      </div>
    </button>
  )
}
